import api from 'services/api';
import { type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

import { schedulingWindowSchema, type SchedulingWindow } from './getSchedulingWindow';
import type { SaveSchedulingWindow } from './postSchedulingWindow';

/**
 * PUT `/scheduling_windows/:zone_name/:window_name` — update the window
 * identified by its **pre-edit** zone and name, which is how this resource is
 * addressed for writes even though the single read is by numeric id. A rename is
 * expressed by `save.name` differing from `windowName`.
 *
 * Both path segments are escaped: a name may contain a `/`, which would otherwise
 * 404 before reaching the route.
 *
 * Returns the updated window, validated against the shared
 * `schedulingWindowSchema` — the same `windowDetailsSQL()` shape as create and
 * read.
 * Rejects on HTTP error — the write did not happen, and the server's complaint
 * is the useful thing to report. Answers with the **unreadable** arm when the
 * write DID happen and its receipt cannot be read: by then the server has
 * committed, so that is not a failed save and must not be reported as one
 * (#1035).
 */
export const putSchedulingWindow = async (zoneName: string, windowName: string, save: SaveSchedulingWindow): Promise<Received<SchedulingWindow>> => {
  const res = await api.put(`/scheduling_windows/${encodeURIComponent(zoneName)}/${encodeURIComponent(windowName)}`, save);
  return readResponse(res.data, { schema: schedulingWindowSchema, source: 'putSchedulingWindow' });
};
